/**
 * The permission catalog and base-role grant sets (docs/01 §2-§3).
 *
 * Single authorship point: migration 0004 seeds `permission_keys` and
 * `role_permissions` from this file (generated SQL, committed), the CI
 * governance check proves catalog keys === registry keys, and a database
 * integration test proves the seeded rows === this file. Changing a grant is
 * therefore always a reviewed migration plus a visible diff here — never a
 * quiet drift.
 *
 * `isScoped: true` means property scope applies (docs/01 §3): enforcement
 * requires a `user_property_access` edit row for the target property IN
 * ADDITION to the key. Portfolio-wide editors (coordinator, admin) pass
 * because the recompute materializes rows for every active property for them.
 */

import type { PermissionKeyDefinition } from './permission-keys.ts';

const T1 = 1 as const;
const T2 = 2 as const;
const T3 = 3 as const;

export const PERMISSION_CATALOG: readonly PermissionKeyDefinition[] = [
  // Administration
  {
    key: 'admin.home.view',
    module: 'admin',
    description: 'See the administration hub',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'admin.permissions.manage',
    module: 'admin',
    description: 'Edit roles and permission sets',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'admin.reference.manage',
    module: 'admin',
    description: 'Manage reference data (types, categories)',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'admin.registry.view',
    module: 'admin',
    description: 'View the feature registry',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'admin.releases.view',
    module: 'admin',
    description: 'View the release ledger',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'admin.users.manage',
    module: 'admin',
    description: 'Approve, disable, and manage users',
    isScoped: false,
    tier: T1,
  },
  {
    // 0108, L-36: the Dev-only QA panel. Reading every tester's feedback is a
    // review duty, so it is its own key, granted to the administrator alone.
    key: 'qa.review',
    module: 'admin',
    description: "Read every tester's QA feedback on Dev (test only)",
    isScoped: false,
    tier: T1,
  },

  // Audit
  {
    key: 'audit.search',
    module: 'audit',
    description: 'Search the data audit ledger',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'audit.sensitive.read',
    module: 'audit',
    description: 'Read encrypted values inside audit events',
    isScoped: false,
    tier: T1,
  },

  // Impersonation
  {
    key: 'impersonation.view_as',
    module: 'impersonation',
    description: 'View the app as another user (read-only)',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'impersonation.act_as',
    module: 'impersonation',
    description: 'Act as another user (mutating, dual-stamped)',
    isScoped: false,
    tier: T2,
  },

  // Environments & data operations
  {
    key: 'env.refresh',
    module: 'env',
    description: 'Run copy-production-to-test',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'data.export',
    module: 'data',
    description: 'Export permission-scoped data',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'data.import.execute',
    module: 'data',
    description: 'Stage and execute imports',
    isScoped: false,
    tier: T1,
  },
  {
    /**
     * ITS OWN KEY (0073, punch 3D.4), for the reason `management.supersede` has
     * one: this rewrites the name of every property, tenant, vendor and contact
     * an administrator ticks, across five modules at once. Under
     * `admin.reference.manage` a person who maintains a capitalisation
     * vocabulary could rename the portfolio, and the access review would not
     * see the difference.
     *
     * It opens the screen; it does not grant a write. Each group of rows is
     * still filtered by that module's own edit key, and a group withheld for
     * want of one is NAMED on screen rather than quietly missing.
     */
    key: 'data.normalize.execute',
    module: 'data',
    description: 'Review and apply the retroactive name capitalization fix',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'recycle.view',
    module: 'recycle',
    description: 'View the recycle bin',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'recycle.restore',
    module: 'recycle',
    description: 'Restore recycled records',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'recycle.purge',
    module: 'recycle',
    description: 'Hard-delete recycled records (reason required)',
    isScoped: false,
    tier: T1,
  },

  // Portfolio
  {
    key: 'properties.view',
    module: 'properties',
    description: 'View properties',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'properties.create',
    module: 'properties',
    description: 'Create properties',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'properties.edit',
    module: 'properties',
    description: 'Edit properties',
    isScoped: true,
    tier: T1,
  },
  {
    key: 'properties.inactivate',
    module: 'properties',
    description: 'Inactivate properties',
    isScoped: true,
    tier: T1,
  },

  // Tenancy
  {
    key: 'tenants.view',
    module: 'tenants',
    description: 'View tenant organizations and tenancies',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'tenants.create',
    module: 'tenants',
    description: 'Create tenant organizations and tenancies',
    // Scoped, and the one key whose two uses genuinely differ: creating an
    // OCCUPANCY at a property is property-scoped (`tenancies_insert` has always
    // enforced it that way), while creating the COMPANY is not — a new tenant
    // is created during deal work, before it occupies anything. The scope-parity
    // test found this flag understating what the policies already did.
    isScoped: true,
    tier: T1,
  },
  {
    key: 'tenants.edit',
    module: 'tenants',
    description: 'Edit tenant organizations and tenancies',
    isScoped: true,
    tier: T1,
  },
  {
    key: 'leases.manage',
    module: 'leases',
    description: 'Create, activate, and revise leases and abstracts',
    isScoped: true,
    tier: T1,
  },

  // Vendors & agreements
  {
    key: 'vendors.view',
    module: 'vendors',
    description: 'View vendors and agreements',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'vendors.create',
    module: 'vendors',
    description: 'Create vendors',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'vendors.edit',
    module: 'vendors',
    description: 'Edit vendors and contacts',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'agreements.create',
    module: 'agreements',
    description: 'Create agreements and utility accounts',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'agreements.edit',
    module: 'agreements',
    description: 'Edit agreements and utility accounts',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'agreements.supersede',
    module: 'agreements',
    description: 'Put agreements in force, supersede and end them',
    isScoped: false,
    tier: T1,
  },
  {
    /**
     * Matt, 2026-08-25: a superseded agreement "probably should not stay
     * editable unless a system admin does it with reason comment".
     *
     * Its own key rather than a proxy for "is an administrator", for the same
     * reason `agreements.supersede` is its own key: the consequential action is
     * named, so it shows up in the role matrix and the access review as itself.
     * Held by system_administrator alone (through ALL_KEYS), and the write still
     * demands a reason on top of the key — the key says who may, the reason says
     * why they did.
     */
    key: 'agreements.amend_superseded',
    module: 'agreements',
    description:
      'Correct closed agreements (superseded, ended, discarded or expired), with a recorded reason',
    isScoped: false,
    tier: T1,
  },

  // Documents
  {
    key: 'documents.view',
    module: 'documents',
    description: 'View documents and the records they are on',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'documents.link',
    module: 'documents',
    description: 'Link a document to a record',
    isScoped: true,
    tier: T1,
  },
  {
    /**
     * Punch 6.1a. The base feature permission for CHANGING a document at all;
     * whether it applies to a PARTICULAR document is then decided by that
     * document's `access_mode`, its owner, or `documents.manage` — one decision,
     * in `app_can_edit_document` (0083), asked by every consumer.
     *
     * `isScoped: false` deliberately: an unattached document has no property, so
     * property scope is not the axis. (`documents.link` above is `isScoped:
     * true` with no property test in its policy — docs/12 §188 records that
     * mismatch; this key does not repeat it.)
     */
    key: 'documents.edit',
    module: 'documents',
    description: 'Edit a document’s details and associations, where its access mode allows',
    isScoped: false,
    tier: T1,
  },
  {
    /**
     * Punch 6.1a corrected. This key is an ADD-ON to `documents.edit`, never a
     * substitute for it: `app_can_edit_document` and `app_can_manage_document`
     * (0083) both require `documents.edit` first, and `documents.manage` then
     * lifts the row-level restriction — reach any document whatever its access
     * mode, and change that access mode.
     *
     * It used to be described as the ordinary add/edit/check permission, which
     * was true of 0027 and false the moment `documents.edit` existed. A custom
     * add-on role (0053) can grant this key on its own, so the description has
     * to say what an isolated grant actually buys: nothing, until the holder
     * also has `documents.edit`.
     */
    key: 'documents.manage',
    module: 'documents',
    description:
      'On top of editing: reach any document whatever its access mode, and set who can change it',
    isScoped: false,
    tier: T1,
  },

  // Management agreements (docs/11 F-2) — TKC Management Services' own
  // contracts. Their own keys rather than `vendors.*` because the rates we BILL
  // an owner are commercially sensitive in a way a vendor's service list is not.
  {
    key: 'management.view',
    module: 'management',
    description: 'View management agreements and the rates they carry',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'management.manage',
    module: 'management',
    description: 'Add and edit management agreements',
    isScoped: false,
    tier: T1,
  },
  {
    /**
     * Its own key, for the reason `agreements.supersede` is its own key (0049):
     * superseding decides which contract governs TODAY and retires the one that
     * governed yesterday, which is a bigger act than correcting a rate. Naming
     * it means it appears in the role matrix and the quarterly access review as
     * itself rather than being smuggled in under `management.manage`.
     */
    key: 'management.supersede',
    module: 'management',
    description: 'Supersede a management agreement, filing its replacement',
    isScoped: false,
    tier: T1,
  },

  // Notes (portfolio-wide for editor-class roles, ADR-07 — not property-scoped)
  {
    key: 'notes.create',
    module: 'notes',
    description: 'Create notes on records',
    // Scoped as of 0036: a note whose record belongs to a property is written
    // within that property's scope; a note on a vendor or a document has no
    // property and falls to the key alone.
    isScoped: true,
    tier: T1,
  },
  { key: 'notes.edit', module: 'notes', description: 'Edit notes', isScoped: true, tier: T1 },
  {
    key: 'notes.inactivate',
    module: 'notes',
    description: 'Inactivate notes',
    isScoped: true,
    tier: T1,
  },

  // Work
  {
    /**
     * Punch 6.1b. This key now gates the whole Action-items area, INSIGHTS
     * INCLUDED: `/work/insights`, `GET /api/v1/insights`, the occurrence read
     * policy, and — with `insights.ack` — acknowledging or snoozing one.
     * Describing it as "View tasks" understated what holding it grants and what
     * losing it takes away.
     */
    key: 'tasks.view',
    module: 'tasks',
    description: 'View action items, including computed insights',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'tasks.manage',
    module: 'tasks',
    description: 'Create, transfer, and complete tasks',
    // Portfolio-wide per the decided matrix (docs/12 §3): every role that holds
    // tasks at all holds them everywhere. It was marked scoped for a module
    // that does not exist yet (O-28); when tasks land, the scope-parity test
    // forces this back to true if their policies property-test the key.
    isScoped: false,
    tier: T1,
  },
  {
    /**
     * REASSIGN an action item — take it off somebody else's list (ADR-51).
     *
     * Matt, 2026-08-25, on whether a maintenance technician may assign work:
     * "should be able to ask others to join them but not sure we want them to
     * reassign to others, probably only close."
     *
     * That splits one verb into two. CLAIM (add yourself) and JOIN (add someone
     * alongside the existing owners) are ADDITIVE — nobody loses work, and
     * pulling in help is how a technician gets a second pair of hands without a
     * coordinator in the loop. Both ride `tasks.manage`. REASSIGN is DIRECTIVE:
     * it is a decision about another person's day, so it needs this key, which
     * maintenance_technician does not hold.
     *
     * Portfolio-wide like the rest of the tasks module: every role that holds
     * tasks at all holds them everywhere (docs/12 §3).
     */
    key: 'tasks.assign',
    module: 'tasks',
    description: 'Reassign an action item away from its current owner',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'insights.ack',
    module: 'insights',
    description: 'Acknowledge and snooze insights',
    isScoped: false,
    tier: T1,
  },

  // Contacts, search, design
  {
    key: 'contacts.view',
    module: 'contacts',
    description: 'View the internal contacts directory',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'search.global',
    module: 'search',
    description: 'Use global search',
    isScoped: false,
    tier: T1,
  },
  {
    key: 'design.view',
    module: 'design',
    description: 'View the living design-system screen',
    isScoped: false,
    tier: T1,
  },

  // Security codes. Deliberately NOT scoped keys (ADR-30): codes are field data
  // an on-call technician or a manager covering PTO needs at a door they were
  // sent to this morning, so access follows the key portfolio-wide and the
  // control is the audited reveal, not the assignment.
  {
    key: 'security.codes.view',
    module: 'security',
    description: 'Reveal masked security codes (audited)',
    // Deliberately unscoped, twice over: ADR-30 (the person at the door is
    // routinely not the assigned manager) and now docs/12 §3a, which puts
    // reading in the approved-user baseline. The EIN is the one carve-out and
    // has its own key.
    isScoped: false,
    tier: T1,
  },
  {
    key: 'ein.view',
    module: 'security',
    description: "Reveal a federal EIN (the ownership entity's tax identifier)",
    // Split out of security.codes.view by 0036 (docs/12 §5 Q6). A property's or
    // group's federal EIN is a tax identifier used by accounting; a lockbox code
    // is needed at a door at 11pm. One key for both meant putting codes in the
    // read baseline would have handed every user the EINs.
    isScoped: false,
    tier: T1,
  },
  {
    key: 'security.codes.manage',
    module: 'security',
    description: 'Edit security codes (audited)',
    // Scoped as of 0036: ADR-30's no-property-test was about REVEAL. Changing a
    // code is a different act, and docs/12's matrix scopes it to the properties
    // you are assigned to.
    isScoped: true,
    tier: T1,
  },

  // Later tiers, registered now so the catalog matches the registry
  {
    key: 'conversations.view',
    module: 'admin',
    description: 'Read conversations',
    isScoped: false,
    tier: T2,
  },
  {
    key: 'conversations.participate',
    module: 'admin',
    // Split from viewing by 0037. One key covered both, so under it a read-only
    // user could either post or not see conversations at all — and Matt asked
    // for exactly the third thing: "everyone should have read-only access to
    // view conversations across all properties."
    description: 'Post in conversations',
    isScoped: false,
    tier: T2,
  },
  {
    key: 'invoicing.admin',
    module: 'invoicing',
    description: 'Run the invoicing module',
    isScoped: false,
    tier: T3,
  },
  {
    key: 'invoicing.view',
    module: 'invoicing',
    description: 'Read invoices and ledgers',
    isScoped: false,
    tier: T3,
  },
  {
    key: 'reports.view',
    module: 'reports',
    description: 'View reports',
    isScoped: false,
    tier: T3,
  },
];

const ALL_KEYS = PERMISSION_CATALOG.map((p) => p.key);

/**
 * Base-role grant sets, mapped from the docs/01 §2 grant summaries. The
 * system_administrator holds every key; scoped keys still confine other roles
 * to their materialized property access.
 */
/** Read + collaborate: what every approved user can do (docs/12 §3a). */
const BASELINE = [
  'properties.view',
  'tenants.view',
  'vendors.view',
  'documents.view',
  'management.view',
  'contacts.view',
  'tasks.view',
  'insights.ack',
  'search.global',
  'reports.view',
  // ADR-30 twice over, then docs/12 §3a. The federal EIN is deliberately NOT
  // here: it has its own key, because a tax identifier and a lockbox code
  // needed at a door at 11pm are not the same class of fact.
  'security.codes.view',
  // Everyone reads conversations across the whole portfolio (Matt 2026-08-24);
  // POSTING is a separate key that read_only does not hold.
  'conversations.view',
  // The export control lives on the payload rather than the person (docs/12 §5
  // Q3): an export is the filtered view you were looking at, sensitive values
  // never travel, and every export is audited.
  'data.export',
] as const;

/**
 * Base-role grant sets. docs/12 §3 is the matrix these implement, and the
 * matrix in that document is GENERATED from this object by
 * `scripts/ci/check-role-matrix.ts` — so the two cannot drift.
 *
 * Every base role starts from BASELINE, which means each list below reads as
 * exactly what that role can DO beyond looking. That is the payoff of deciding
 * that reading is universal: the interesting part of a role is short.
 *
 * Six base roles, mapping Matt's eight names (2026-08-24). Two of his eight —
 * Invoicing Admin and Invoicing Viewer — are not kinds of person but
 * capabilities that a coordinator, a specialist or an admin might each hold, so
 * they are add-ons.
 */
export const ROLE_GRANTS: Readonly<Record<string, readonly string[]>> = {
  system_administrator: ALL_KEYS,

  /**
   * "Access to manage all property records but no admin functions designated
   * for system admins only." The portfolio-wide operator: everything a Property
   * Manager can do, everywhere, plus creating and retiring the records
   * themselves, the taxonomies, and imports.
   */
  service_coordinator: [
    ...BASELINE,
    'conversations.participate',
    'properties.create',
    'properties.edit',
    'properties.inactivate',
    'tenants.create',
    'tenants.edit',
    'leases.manage',
    'vendors.create',
    'vendors.edit',
    'agreements.create',
    'agreements.edit',
    'agreements.supersede',
    'management.manage',
    // Superseding is the coordinator's call for the same reason
    // `agreements.supersede` is: choosing which contract governs today is
    // portfolio work. A property manager holds neither — they read the
    // management agreement and cannot change what TKC bills.
    'management.supersede',
    'documents.link',
    'documents.edit',
    'documents.manage',
    'notes.create',
    'notes.edit',
    'notes.inactivate',
    'tasks.manage',
    'tasks.assign',
    'security.codes.manage',
    'ein.view',
    'admin.reference.manage',
    'data.import.execute',
  ],

  /**
   * "Properties assigned to them that they can manage data of, and also work
   * with all vendor data, and read-only access to properties not assigned to
   * them." The scoped keys below bind to their assignments; the vendor and
   * agreement keys do not, because a vendor is a portfolio-level record that
   * references properties rather than belonging to one.
   */
  property_manager: [
    ...BASELINE,
    'conversations.participate',
    'properties.edit',
    // Matt 2026-08-24, asked directly: a PM should be able to seat a tenant at
    // their own building rather than waiting on a coordinator. They already held
    // `tenants.edit` and `leases.manage` — so they could rewrite an existing
    // tenant and manage its lease, but a NEW tenant signing at their property
    // needed somebody else. The property scope still contains them: a TENANCY
    // is scoped by property_id (docs/12 §4), so a PM can only seat a tenant
    // where they are assigned. The company record itself is portfolio-level by
    // shape, exactly as `vendors.create` already is for this role. (0041)
    'tenants.create',
    'tenants.edit',
    'leases.manage',
    'vendors.create',
    'vendors.edit',
    'agreements.create',
    'agreements.edit',
    'agreements.supersede',
    'documents.link',
    // Punch 6.1a: "Everyone can edit" has to MEAN somebody. A property manager
    // files documents against their buildings all day; excluding them would
    // make the label a promise the grants do not keep (docs/12 says so out loud).
    'documents.edit',
    'notes.create',
    'notes.edit',
    'notes.inactivate',
    'tasks.manage',
    'tasks.assign',
    'security.codes.manage',
    // Matt 2026-08-24: "property managers should also be able to view EINs as
    // well" — the tax id of the entity that owns a property they manage.
    'ein.view',
    // Reads the management agreement, cannot change the fee: a PM has to answer
    // "are we obliged to do this?" without being able to alter what we bill.
  ],

  /**
   * "Ability to read all property data, limited write to properties they have
   * been assigned to: adding notes, changing/updating codes, alarm codes. Can
   * read vendor data, vendor contact data, agreements and tenant data but cannot
   * change vendor or tenant contact data."
   *
   * The second scoped role, and the reason F-A had to be fixed before this file
   * could name it — the policy engine used to decide scope by role name.
   */
  maintenance_manager: [
    ...BASELINE,
    'conversations.participate',
    'notes.create',
    'notes.edit',
    'documents.link',
    'documents.edit',
    'tasks.manage',
    'tasks.assign',
    'security.codes.manage',
  ],

  /**
   * "Mostly read only for all properties but may interact in conversations if
   * needed", plus tasks (Matt 2026-08-24: "Maintenance Techs may get tasks").
   * The on-call rotation that can send them to any property tonight is exactly
   * why reading — codes included — is portfolio-wide (ADR-30).
   */
  maintenance_technician: [
    ...BASELINE,
    'conversations.participate',
    'notes.create',
    'notes.edit',
    'tasks.manage',
  ],

  /**
   * "Can only read data in the system, across the entire app, and can view
   * invoice data — can't change anything."
   *
   * The only base role with no write key at all, and the one place the docs/01
   * §2 collaboration floor narrows: it reads conversations without posting in
   * them. `invoicing.view` is here because Matt named it for this role
   * specifically; it is not in BASELINE, because billing rates and tax codes are
   * not the property information his read-everything sentence enumerated.
   */
  read_only: [...BASELINE, 'invoicing.view'],
};

/**
 * Add-on grant sets. An add-on is a capability, not a person: an invoicing-only
 * hire is `read_only` + `invoicing_administrator`.
 */
export const ADDON_GRANTS: Readonly<Record<string, readonly string[]>> = {
  /**
   * "Full access to work with invoices, create invoice periods, import Corrigo
   * labor reports for invoice periods, change billing rates, rules, taxable
   * fields, tax rates and codes." `ein.view` rides along because an invoice run
   * is precisely where a tax identifier is needed.
   */
  invoicing_administrator: ['invoicing.admin', 'invoicing.view', 'ein.view'],

  /** "View only access to invoice module." */
  invoice_viewer: ['invoicing.view'],
};
